import React, { createContext, useContext, useEffect, useState } from 'react';
import { Voucher } from '../../types/Voucher';
import { VoucherFilter } from '../../types/VoucherFilter';
import { getVouchers, updateVoucherComment } from '../../apis';
import { useDebounce } from '../../helpers';

type VoucherListContextType = {
  vouchers: Voucher[];
  filters: VoucherFilter;
  loading: boolean;
  totalCount: number;
  totalPages: number;
  setFilters: React.Dispatch<React.SetStateAction<VoucherFilter>>;
  refetch: () => void;
  // eslint-disable-next-line no-unused-vars
  addOrUpdateComment: (voucherId: string, comment: string, commentId?: string) => Promise<void>;
};

const VoucherListContext = createContext<VoucherListContextType | undefined>(undefined);

export const useVoucherList = () => {
  const context = useContext(VoucherListContext);
  if (!context) {
    throw new Error('useVoucherList must be used within VoucherListProvider');
  }
  return context;
};

export const VoucherListProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [filters, setFilters] = useState<VoucherFilter>({
    limit: 25,
    page: 1,
    code: '',
    createdBy: '',
    status: '',
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const debouncedCode = useDebounce(filters.code, 1000);
  const debouncedCreatedBy = useDebounce(filters.createdBy, 1000);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getVouchers({
        code: debouncedCode,
        createdBy: debouncedCreatedBy,
        status: filters.status,
        page: filters.page,
        limit: filters.limit,
      });
      setVouchers(data.results);
      setTotalCount(data.count);
      setTotalPages(Math.ceil(data.count / (filters.limit || 10)));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addOrUpdateComment = async (voucherId: string, comment: string, commentId?: string) => {
    try {
      const newComment = await updateVoucherComment({ voucherId, comment, commentId });

      setVouchers(prev =>
        prev.map(v =>
          v.id === voucherId
            ? {
                ...v,
                comments: [...(v.comments || []), newComment],
              }
            : v,
        ),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to add/update comment:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCode, debouncedCreatedBy, filters.status, filters.page, filters.limit]);

  return (
    <VoucherListContext.Provider
      value={{
        vouchers,
        filters,
        loading,
        totalCount,
        totalPages,
        setFilters,
        refetch: fetchData,
        addOrUpdateComment,
      }}
    >
      {children}
    </VoucherListContext.Provider>
  );
};
